/**
 * V1 to V2 Migration
 *
 * Transforms deprecated V1 Triggers + Actions behaviors into BehaviorGraph,
 * and normalizes legacy data shapes that survived the Unity-era export format.
 *
 * V1 Format:
 *   - Triggers behavior: { "@type": "Triggers", triggers: [{ on, target, action }] }
 *   - Actions behavior: { "@type": "Actions", actions: [{ name, target, gameObjectState?, toggle?, animation?, video?, then? }] }
 *   - Cycler behavior: { "@type": "Cycler" } - cycles through child objects
 *   - Collider vectors: { x, y, z } objects (now [x, y, z] arrays)
 *
 * V2 Format:
 *   - BehaviorGraph behavior with GraphData nodes
 *   - Cycler → Pages behavior + BehaviorGraph with pages/next, pages/prev
 *   - Vec3 fields as tuple arrays
 */

import { BehaviorData } from '../scenedata/behaviordata';
import { TriggersData, TriggerEventType } from '../scenedata/legacy/triggersdata';
import { ActionsData, ActionData } from '../scenedata/legacy/actionsdata';
import type { BehaviorGraphData, GraphData, GraphNode, GraphVariable, Declaration, TypeDefinition } from '../scenedata/behaviors/behaviorgraphdata';
import { GraphOps } from '../scenedata/behaviors/behaviorgraphdata';
import { CyclerData } from '../scenedata/legacy/stubs';
import { PagesData } from '../scenedata/behaviors/pagesdata';
import { Logger } from '../utils/Logger';
import { WrapMode } from '../scenedata/common';

// ============================================================================
// Graph builder helpers (data-only, no SceneObject refs needed)
// ============================================================================

interface GraphBuildState {
  types: TypeDefinition[];
  declarations: Declaration[];
  nodes: GraphNode[];
  variables: GraphVariable[];
  declMap: Map<string, number>;
  /** Toggle action name → index into variables[] (one bool per V1 toggle action) */
  toggleVarMap: Map<string, number>;
}

function getOrAddDecl(state: GraphBuildState, op: string): number {
  let idx = state.declMap.get(op);
  if (idx === undefined) {
    idx = state.declarations.length;
    state.declarations.push({ op });
    state.declMap.set(op, idx);
  }
  return idx;
}

function addNode(state: GraphBuildState, op: string, config?: Record<string, unknown[]>): number {
  const declIdx = getOrAddDecl(state, op);
  const configuration: Record<string, { type: number; value: unknown[] }> | undefined =
    config ? Object.fromEntries(
      Object.entries(config).map(([k, v]) => [k, { type: 2, value: v }])
    ) : undefined;
  const idx = state.nodes.length;
  state.nodes.push({ declaration: declIdx, ...(configuration ? { configuration } : {}) });
  return idx;
}

function setFlow(state: GraphBuildState, from: number, fromSocket: string, to: number, toSocket = 'in'): void {
  const node = state.nodes[from];
  if (!node.flows) node.flows = {};
  node.flows[fromSocket] = { node: to, socket: toSocket };
}

function setValue(state: GraphBuildState, nodeIdx: number, socket: string, value: unknown): void {
  const node = state.nodes[nodeIdx];
  if (!node.values) node.values = {};
  const typeIdx = typeof value === 'boolean' ? 0 : typeof value === 'number' ? 1 : 2;
  node.values[socket] = { type: typeIdx, value: [value] };
}

/** Wire a data input from another node's output socket. */
function setValueRef(state: GraphBuildState, nodeIdx: number, socket: string, sourceNode: number, sourceSocket: string): void {
  const node = state.nodes[nodeIdx];
  if (!node.values) node.values = {};
  node.values[socket] = { node: sourceNode, socket: sourceSocket };
}

function createGraphState(): GraphBuildState {
  return {
    types: [
      { signature: 'bool' },
      { signature: 'float' },
      { signature: 'string' },
    ],
    declarations: [],
    nodes: [],
    variables: [],
    declMap: new Map(),
    toggleVarMap: new Map(),
  };
}

/**
 * Allocate (or reuse) a `bool` graph variable for a V1 toggle action.
 * The variable holds the toggle's current on/off state, initialized to `isOn`.
 */
function getOrAddToggleVariable(state: GraphBuildState, toggleName: string, initialOn: boolean): number {
  let idx = state.toggleVarMap.get(toggleName);
  if (idx !== undefined) return idx;
  idx = state.variables.length;
  state.variables.push({
    name: `toggle_${toggleName}`,
    type: 0, // bool — index into the standard [bool, float, string] type table
    value: [initialOn],
  });
  state.toggleVarMap.set(toggleName, idx);
  return idx;
}

function buildGraphData(state: GraphBuildState): GraphData {
  // Compact types to only those actually referenced by nodes or variables
  const SIGNATURES = ['bool', 'float', 'string'] as const;
  const usedIndices = new Set<number>();

  for (const node of state.nodes) {
    if (node.configuration) {
      for (const cv of Object.values(node.configuration)) usedIndices.add(cv.type);
    }
    if (node.values) {
      for (const v of Object.values(node.values)) {
        if ('type' in v) usedIndices.add(v.type);
      }
    }
  }
  for (const v of state.variables) usedIndices.add(v.type);

  const sorted = [...usedIndices].sort((a, b) => a - b);
  const types = sorted.map(i => ({ signature: SIGNATURES[i] }));
  const remap = new Map<number, number>();
  sorted.forEach((old, compact) => remap.set(old, compact));

  // Remap type indices in nodes
  const nodes = state.nodes.map(node => {
    const remapped = { ...node };
    if (remapped.configuration) {
      remapped.configuration = Object.fromEntries(
        Object.entries(remapped.configuration).map(([k, v]) => [k, { ...v, type: remap.get(v.type) ?? 0 }])
      );
    }
    if (remapped.values) {
      remapped.values = Object.fromEntries(
        Object.entries(remapped.values).map(([k, v]) => [k, 'type' in v ? { ...v, type: remap.get(v.type) ?? 0 } : v])
      );
    }
    return remapped;
  });

  const variables = state.variables.map(v => ({ ...v, type: remap.get(v.type) ?? 0 }));

  return {
    types,
    declarations: state.declarations,
    variables,
    events: [],
    nodes,
  };
}

// ============================================================================
// V1 type guards
// ============================================================================

function isTriggersData(behavior: BehaviorData): behavior is TriggersData {
  return behavior['@type'] === 'Triggers';
}

function isActionsData(behavior: BehaviorData): behavior is ActionsData {
  return behavior['@type'] === 'Actions';
}

function isCyclerData(behavior: BehaviorData): behavior is CyclerData {
  return behavior['@type'] === 'Cycler';
}

/**
 * Convert V1 event type to graph event op
 */
function eventOpForV1Type(v1Event: TriggerEventType | string): string {
  switch (v1Event) {
    case 'Select':
      return GraphOps.EVENT_ON_SELECT;
    default:
      Logger.warn(`[V1ToV2Migration] Unknown V1 event type "${v1Event}", falling back to onSelect`);
      return GraphOps.EVENT_ON_SELECT;
  }
}

// ============================================================================
// V1 action → graph nodes
// ============================================================================

/**
 * Convert a V1 action to graph nodes, returning the first execution node index.
 * Returns -1 if the action type is not recognized.
 */
function emitV1Action(
  state: GraphBuildState,
  v1Action: ActionData,
  actionMap: Map<string, ActionData>,
  visited: Set<string>,
): number {
  if (visited.has(v1Action.name)) {
    Logger.log(`[V1ToV2Migration] Circular action reference detected: ${v1Action.name}`);
    return -1;
  }
  visited.add(v1Action.name);

  let nodeIdx = -1;

  if (v1Action.gameObjectState) {
    const target = v1Action.target || '';
    nodeIdx = addNode(state, GraphOps.TRACE_SET_ACTIVE, target ? { target: [target] } : undefined);
    setValue(state, nodeIdx, 'state', v1Action.gameObjectState.targetState ?? true);
  } else if (v1Action.toggle) {
    // Toggle: persist on/off state in a graph variable; alternate between
    // onAction and offAction on successive triggers.
    //   bool state = isOn  // initial
    //   on trigger:
    //     if (state)  → state = false; run offAction (incl. its `then` chain)
    //     else        → state = true;  run onAction  (incl. its `then` chain)
    // The variable flip happens *before* the sub-action so we don't have to
    // walk through the subtree to find its terminal node — sub-actions can be
    // chained via `then` (e.g. Show → Show_Bg), and that chain wires the
    // subtree's tail to its own continuation.
    const v1Toggle = v1Action.toggle;
    const varIdx = getOrAddToggleVariable(state, v1Action.name, v1Toggle.isOn ?? false);

    const getVar = addNode(state, GraphOps.VARIABLE_GET, { variable: [varIdx] });
    const branch = addNode(state, GraphOps.FLOW_BRANCH);
    setValueRef(state, branch, 'condition', getVar, 'value');

    const onAction = v1Toggle.onAction ? actionMap.get(v1Toggle.onAction) : undefined;
    const offAction = v1Toggle.offAction ? actionMap.get(v1Toggle.offAction) : undefined;

    // false branch: state = true; run onAction
    if (onAction) {
      const setTrue = addNode(state, GraphOps.VARIABLE_SET, { variable: [varIdx] });
      setValue(state, setTrue, 'value', true);
      setFlow(state, branch, 'false', setTrue);
      const onNode = emitV1Action(state, onAction, actionMap, new Set(visited));
      if (onNode >= 0) setFlow(state, setTrue, 'out', onNode);
    }

    // true branch: state = false; run offAction
    if (offAction) {
      const setFalse = addNode(state, GraphOps.VARIABLE_SET, { variable: [varIdx] });
      setValue(state, setFalse, 'value', false);
      setFlow(state, branch, 'true', setFalse);
      const offNode = emitV1Action(state, offAction, actionMap, new Set(visited));
      if (offNode >= 0) setFlow(state, setFalse, 'out', offNode);
    }

    nodeIdx = branch;
  } else if (v1Action.animation) {
    const v1Anim = v1Action.animation;
    const config: Record<string, unknown[]> = {};
    if (v1Action.target) config['target'] = [v1Action.target];
    if (v1Anim.playClip) config['clip'] = [v1Anim.playClip];
    nodeIdx = addNode(state, GraphOps.TRACE_PLAY_ANIMATION, Object.keys(config).length > 0 ? config : undefined);
    const isLoop = v1Anim.wrapMode === WrapMode.Loop;
    if (isLoop) setValue(state, nodeIdx, 'loop', true);
  } else if (v1Action.video) {
    const v1Video = v1Action.video;
    const config: Record<string, unknown[]> = {};
    if (v1Action.target) config['target'] = [v1Action.target];
    // V1 only carries a play/pause boolean for video, so map to the
    // appropriate single-purpose media block.
    const op = v1Video.play === false ? GraphOps.TRACE_PAUSE_MEDIA : GraphOps.TRACE_PLAY_MEDIA;
    nodeIdx = addNode(state, op, Object.keys(config).length > 0 ? config : undefined);
  }

  // Handle action chaining via 'then'
  if (nodeIdx >= 0 && v1Action.then && v1Action.then !== '') {
    const nextAction = actionMap.get(v1Action.then);
    if (nextAction) {
      const nextIdx = emitV1Action(state, nextAction, actionMap, new Set(visited));
      if (nextIdx >= 0) {
        // For animation, chain via 'done' (wait for completion); otherwise 'out'
        const outSocket = v1Action.animation ? 'done' : 'out';
        setFlow(state, nodeIdx, outSocket, nextIdx);
      }
    }
  }

  return nodeIdx;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Migrate V1 Triggers + Actions behaviors to a BehaviorGraph behavior.
 *
 * @param behaviors Array of behavior data for a scene object
 * @param objectName Name of the scene object (for logging)
 * @returns Modified behaviors array with BehaviorGraph if migration occurred
 */
export function migrateV1ToV2Interactions(behaviors: BehaviorData[], objectName: string): BehaviorData[] {
  const triggersData = behaviors.find(isTriggersData);
  const actionsData = behaviors.find(isActionsData);

  if (!triggersData && !actionsData) {
    return behaviors;
  }

  // Skip if already has BehaviorGraph
  if (behaviors.some(b => b['@type'] === 'BehaviorGraph')) {
    Logger.log(`[V1ToV2Migration] Object "${objectName}" already has BehaviorGraph, skipping migration`);
    return behaviors;
  }

  Logger.log(`[V1ToV2Migration] Migrating V1 interactions for object: ${objectName}`);

  // Build action lookup map
  const actionMap = new Map<string, ActionData>();
  if (actionsData) {
    for (const action of actionsData.actions) {
      actionMap.set(action.name, action);
    }
  }

  const state = createGraphState();
  let migratedCount = 0;

  if (triggersData) {
    for (const trigger of triggersData.triggers) {
      const referencedAction = actionMap.get(trigger.action);
      if (!referencedAction) {
        Logger.log(`[V1ToV2Migration] Warning: Trigger references unknown action "${trigger.action}" on object "${objectName}"`);
        continue;
      }

      // Emit event node
      const eventOp = eventOpForV1Type(trigger.on);
      const eventNode = addNode(state, eventOp);

      // Emit action nodes
      const actionNode = emitV1Action(state, referencedAction, actionMap, new Set());
      if (actionNode < 0) {
        Logger.log(`[V1ToV2Migration] Warning: Could not convert action "${trigger.action}" on object "${objectName}"`);
        continue;
      }

      setFlow(state, eventNode, 'out', actionNode);
      migratedCount++;
    }
  }

  if (migratedCount === 0 && state.nodes.length === 0) {
    // Nothing to migrate — just remove V1 behaviors
    return behaviors.filter(b => !isTriggersData(b) && !isActionsData(b));
  }

  const graphData: BehaviorGraphData = {
    '@type': 'BehaviorGraph',
    graph: buildGraphData(state),
  };

  const migratedBehaviors = behaviors.filter(b => !isTriggersData(b) && !isActionsData(b));
  migratedBehaviors.push(graphData);

  Logger.log(`[V1ToV2Migration] Migrated ${triggersData?.triggers?.length || 0} triggers and ${actionMap.size} actions to BehaviorGraph for "${objectName}"`);

  return migratedBehaviors;
}

/**
 * Check if a behaviors array contains V1 interaction behaviors
 */
export function hasV1Behaviors(behaviors: BehaviorData[] | undefined): boolean {
  if (!behaviors) return false;
  return behaviors.some(b => isTriggersData(b) || isActionsData(b));
}

// ============================================================================
// CROSS-OBJECT MIGRATION
// ============================================================================

/**
 * Scene-wide migration of V1 Triggers + Actions that live on different objects.
 *
 * @param objectBehaviors Map of object name → behaviors array (mutated in place)
 */
export function migrateV1CrossObjectInteractions(
  objectBehaviors: Map<string, BehaviorData[]>
): void {
  // Pass 1: Build global action map
  const actionsByObject = new Map<string, Map<string, ActionData>>();
  const consumedActionObjects = new Set<string>();

  for (const [objectName, behaviors] of objectBehaviors) {
    const actionsData = behaviors.find(isActionsData);
    if (actionsData) {
      const actionMap = new Map<string, ActionData>();
      for (const action of actionsData.actions) {
        actionMap.set(action.name, action);
      }
      actionsByObject.set(objectName, actionMap);
    }
  }

  // Pass 2: Process triggers that reference actions on other objects
  for (const [objectName, behaviors] of objectBehaviors) {
    const triggersData = behaviors.find(isTriggersData);
    if (!triggersData) continue;

    // Skip if already has BehaviorGraph
    if (behaviors.some(b => b['@type'] === 'BehaviorGraph')) continue;

    // If has own Actions, let per-object migration handle it
    if (behaviors.some(isActionsData)) continue;

    const state = createGraphState();

    for (const trigger of triggersData.triggers) {
      const targetObjectName = trigger.target;
      const targetActionMap = actionsByObject.get(targetObjectName);

      if (!targetActionMap) {
        Logger.log(`[V1ToV2Migration] Cross-object: trigger on "${objectName}" references unknown action object "${targetObjectName}"`);
        continue;
      }

      const referencedAction = targetActionMap.get(trigger.action);
      if (!referencedAction) {
        Logger.log(`[V1ToV2Migration] Cross-object: trigger on "${objectName}" references unknown action "${trigger.action}" on "${targetObjectName}"`);
        continue;
      }

      const eventOp = eventOpForV1Type(trigger.on);
      const eventNode = addNode(state, eventOp);

      const actionNode = emitV1Action(state, referencedAction, targetActionMap, new Set());
      if (actionNode < 0) continue;

      setFlow(state, eventNode, 'out', actionNode);
      consumedActionObjects.add(targetObjectName);
    }

    if (state.nodes.length > 0) {
      const migratedBehaviors = behaviors.filter(b => !isTriggersData(b));
      migratedBehaviors.push({
        '@type': 'BehaviorGraph',
        graph: buildGraphData(state),
      } as BehaviorGraphData);
      objectBehaviors.set(objectName, migratedBehaviors);

      Logger.log(`[V1ToV2Migration] Cross-object: migrated interactions for "${objectName}"`);
    }
  }

  // Pass 3: Clean up consumed Actions-only objects
  for (const actionObjectName of consumedActionObjects) {
    const behaviors = objectBehaviors.get(actionObjectName);
    if (behaviors) {
      objectBehaviors.set(actionObjectName, behaviors.filter(b => !isActionsData(b)));
    }
  }
}

// ============================================================================
// CYCLER MIGRATION
// ============================================================================

/**
 * Migrate V1 Cycler behavior to Pages behavior.
 * The Pages behavior has built-in next()/prev() methods — no graph needed
 * for the basic autoplay case.
 *
 * @param behaviors Array of behavior data for a scene object
 * @param objectName Name of the scene object (for logging)
 * @returns Modified behaviors array with Pages if migration occurred
 */
export function migrateV1CyclerToV2(behaviors: BehaviorData[], objectName: string): BehaviorData[] {
  const cyclerData = behaviors.find(isCyclerData);
  if (!cyclerData) {
    return behaviors;
  }

  // Skip if already has Pages
  if (behaviors.some(b => b['@type'] === 'Pages')) {
    Logger.log(`[V1ToV2Migration] Object "${objectName}" already has Pages, skipping Cycler migration`);
    return behaviors;
  }

  Logger.log(`[V1ToV2Migration] Migrating V1 Cycler to Pages for object: ${objectName}`);

  const pagesData: PagesData = {
    '@type': 'Pages',
  };

  const migratedBehaviors = behaviors.filter(b => !isCyclerData(b));
  migratedBehaviors.push(pagesData);

  Logger.log(`[V1ToV2Migration] Migrated Cycler to Pages for "${objectName}"`);

  return migratedBehaviors;
}

/**
 * Check if a behaviors array contains V1 Cycler behavior
 */
export function hasV1Cycler(behaviors: BehaviorData[] | undefined): boolean {
  if (!behaviors) return false;
  return behaviors.some(isCyclerData);
}

// ============================================================================
// Legacy vector field normalization
// ============================================================================

// Per-behavior whitelist for legacy {x,y,z[,w]} → [x,y,z[,w]] conversion.
// Limited to known-vector fields so we don't rewrite unrelated object-shaped
// data (e.g. ParticleSystem.size, which is a curve descriptor).
const LEGACY_VECTOR_FIELDS: Record<string, readonly string[]> = {
  BoxCollider: ['size', 'center'],
  SphereCollider: ['center'],
};

/**
 * Convert legacy {x, y, z[, w]} vector objects to [x, y, z[, w]] tuples in place
 * for whitelisted behavior fields. No-op for behaviors not in the whitelist.
 */
export function normalizeLegacyVectorFields(component: BehaviorData): void {
  const fields = LEGACY_VECTOR_FIELDS[component['@type']];
  if (!fields) return;
  const data = component as unknown as Record<string, unknown>;
  for (const field of fields) {
    const value = data[field];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>;
      if (typeof obj.x === 'number' && typeof obj.y === 'number' && typeof obj.z === 'number') {
        data[field] = typeof obj.w === 'number'
          ? [obj.x, obj.y, obj.z, obj.w]
          : [obj.x, obj.y, obj.z];
      }
    }
  }
}
